import React, { useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import OsScroll from '../ui/OsScroll';
import { showsColumnProgress } from '../../extensions/bundled/task-manager/boardProgress';
import { sortListTasks, SORT_MANUAL } from '../../extensions/bundled/task-manager/listViewSort';
import { AddListDropZone } from './ColumnDropZone';
import ListColumnHeader from './ListColumnHeader';
import ListSection from './ListSection';
import useListViewPrefs from './useListViewPrefs';
import useGroupTasks from './useGroupTasks';
import './ListView.css';

/**
 * The open board in List mode (Boards Redesign list 6, 12.10.2026): a sticky
 * column header (card 6.2) over one collapsible section per list (card 6.1),
 * rows drawn in the chosen sort order (card 6.3). Collapsed sections and the
 * sort are per device (useListViewPrefs → localStorage), never synced.
 *
 * `taskVisible` / `showDoneTasks` are the board's filter and "show completed"
 * toggle; progress always counts every task of a list (groupProgress).
 * A non-manual sort turns row dragging off (the sort would put a dropped row
 * straight back); sections can still be reordered.
 */
export default function ListView({ board, groups, showDoneTasks = true, taskVisible, isFiltering = false, isTeamBoard = false, selectedTaskId = null, onAddList, onAddTask, ...sectionProps }) {
  const { sort, collapsed, toggleCollapsed } = useListViewPrefs(board.id);
  // Only the labels: stable across drag steps, so memoised rows skip re-rendering.
  const labelSource = useMemo(() => ({ labels: board.labels }), [board.labels]);
  const sorted = sort !== SORT_MANUAL;
  // Stable per-section arrays: a drag step re-renders only the sections it touched.
  const visible = useCallback((t) => (showDoneTasks || !t.done) && (!taskVisible || taskVisible(t)), [showDoneTasks, taskVisible]);
  const order = useCallback((tasks) => sortListTasks(tasks, sort), [sort]);
  const groupTasks = useGroupTasks(board, groups, visible, order);
  // Adding to a collapsed list opens it, so the new row (in rename mode) is seen.
  const addTask = useCallback(
    (groupId) => {
      if (collapsed.includes(groupId)) toggleCollapsed(groupId);
      onAddTask(groupId);
    },
    [collapsed, toggleCollapsed, onAddTask]
  );

  return (
    <SortableContext items={groups.map((g) => `group:${g.id}`)} strategy={verticalListSortingStrategy}>
      <OsScroll className={`task-board-list task-board-list-view flex-1 min-h-0${isFiltering ? ' task-board-scroll--filtered' : ''}${sorted ? ' task-board-list-view--sorted' : ''}${isTeamBoard ? '' : ' task-board-list-view--no-owner'}`}>
        <ListColumnHeader isTeamBoard={isTeamBoard} />
        {groups.map((group, index) => (
          <ListSection
            key={group.id}
            group={group}
            index={index}
            progress={groupTasks.get(group.id).progress}
            showProgress={showsColumnProgress(board)}
            tasks={groupTasks.get(group.id).tasks}
            collapsed={collapsed.includes(group.id)}
            onToggleCollapsed={toggleCollapsed}
            filtering={isFiltering}
            dragDisabled={sorted}
            selectedTaskId={selectedTaskId}
            onAddTask={addTask}
            palette={labelSource}
            {...sectionProps}
          />
        ))}
        <AddListDropZone className="task-board-add-column-btn task-board-add-list-btn" onClick={onAddList}>
          <Plus size={14} /> Add list
        </AddListDropZone>
      </OsScroll>
    </SortableContext>
  );
}
